import { Controller } from "@hotwired/stimulus"

// Borne défensive pour `refresh()` : une chaîne de conditions purement
// positive se stabilise en quelques passes, mais une négation prise dans un
// cycle (deux questions qui se masquent l'une l'autre) ferait alterner la
// visibilité indéfiniment. Le YAML n'est pas censé contenir un tel cycle,
// mais on ne fait pas dépendre la sécurité du navigateur d'un garde-fou qui
// vit ailleurs : au-delà de cette borne, on arrête — une configuration
// malformée produit au pire un affichage légèrement faux, jamais un onglet
// gelé.
const MAX_REFRESH_PASSES = 10

// Affichage conditionnel des questions d'audit. Le serveur reste seul juge :
// il purge à l'enregistrement les réponses devenues sans objet. Ici, on ne fait
// qu'éviter au client de voir une question sans rapport avec sa situation.
//
// Un bloc masqué est aussi désactivé, pour qu'il ne soit pas soumis.
export default class extends Controller {
  static targets = ["question", "form"]

  connect() {
    this.refresh()
  }

  // Une seule passe ne suffit pas pour une chaîne de conditions : masquer
  // `server_roles` ne doit compter dans `currentAnswers()` qu'une fois
  // `hypervisor` réévalué à son tour. On boucle donc jusqu'à ce qu'aucun
  // bloc ne change plus de visibilité — un point fixe, atteint en au plus
  // une itération par niveau de profondeur de dépendance (voir
  // `MAX_REFRESH_PASSES` pour le cas où ce point fixe n'existe pas).
  refresh() {
    let stable = false
    let passes = 0

    while (!stable && passes < MAX_REFRESH_PASSES) {
      stable = true
      passes++
      const answers = this.currentAnswers()

      this.questionTargets.forEach((question) => {
        const raw = question.dataset.showIf
        const visible = !raw || raw === "null" || this.satisfied(JSON.parse(raw), answers)

        if (question.hidden === visible) stable = false
        question.hidden = !visible
        question.querySelectorAll("input, select, textarea").forEach((field) => {
          field.disabled = !visible
        })
      })
    }
  }

  // Soumet l'étape en cours avant de laisser le lien naviguer, pour qu'un
  // retour en arrière ne perde jamais une saisie. Ne navigue que si cette
  // soumission a réussi : sur un échec (validation refusée côté serveur),
  // quitter la page effacerait l'erreur sans que le consultant l'ait vue.
  submitBeforeLeaving(event) {
    event.preventDefault()
    const destination = event.currentTarget.href
    this.formTarget.addEventListener("turbo:submit-end", (submitEvent) => {
      if (submitEvent.detail.success) window.location.href = destination
    }, { once: true })
    this.formTarget.requestSubmit()
  }

  // { question_id: valeur } pour les champs actuellement visibles.
  currentAnswers() {
    const answers = {}

    this.questionTargets.forEach((question) => {
      if (question.hidden) return
      const id = question.dataset.questionId
      const checkboxes = question.querySelectorAll("input[type=checkbox]")

      if (checkboxes.length > 0) {
        answers[id] = Array.from(checkboxes).filter((box) => box.checked).map((box) => box.value)
        return
      }

      const radio = question.querySelector("input[type=radio]:checked")
      if (radio) {
        answers[id] = radio.value === "true" ? true : radio.value === "false" ? false : radio.value
        return
      }

      // Étiquettes libres : leurs valeurs vivent dans des champs cachés créés
      // par capture-tags. Ce test passe avant celui du texte, car la question
      // porte aussi un champ de saisie qui, lui, n'est pas une réponse.
      const tags = question.querySelectorAll("input[type=hidden][data-tag-value]")
      if (tags.length > 0) {
        answers[id] = Array.from(tags).map((field) => field.value)
        return
      }

      const field = question.querySelector("input[name][type=text], input[type=number], input[type=date]")
      if (field && field.value !== "") {
        answers[id] = field.type === "number" ? Number(field.value) : field.value
      }
    })

    return answers
  }

  // Même grammaire que ClientAudit::Condition côté Ruby. `spec.blank?` y vaut
  // vrai pour `nil` comme pour `{}` : un objet vide doit donc être traité
  // comme une absence de condition, pas comme une feuille sans clause.
  satisfied(spec, answers) {
    if (!spec || Object.keys(spec).length === 0) return true
    if (spec.all_of) return spec.all_of.every((sub) => this.satisfied(sub, answers))
    if (spec.any_of) return spec.any_of.some((sub) => this.satisfied(sub, answers))
    if (spec.none_of) return !spec.none_of.some((sub) => this.satisfied(sub, answers))

    const value = answers[spec.answer]
    const answered = !(value === undefined || value === null || value === "" || (Array.isArray(value) && value.length === 0))

    if ("present" in spec) return spec.present === answered
    if (!answered) return false
    if ("is" in spec) return value === spec.is
    if ("is_not" in spec) return value !== spec.is_not
    if ("in" in spec) return spec.in.includes(value)
    if ("includes" in spec) return Array.isArray(value) && value.includes(spec.includes)

    return false
  }
}
